import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DropdownButton,
  InfiniteScrollV2,
  DropdownItem,
  Empty,
  Icon,
  TruncatedText,
  useTranslation,
  DebouncedSearchInput,
} from '@testsigma/atoms';
import { ListItemSkeleton } from '@modules/test-development/components/list-item-skeleton';
import noResultFound from '@images/no-result-found.svg';
import {
  SalesforceConnection,
  SalesforceConnectionEdition,
} from '@modules/salesforce/models';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import SalesforceConnectionService from '@modules/salesforce/services/salesforce-connections.service';

const { VirtualizedInfiniteLoader } = InfiniteScrollV2;

interface SalesforceConnectionDropdownItemProps {
  connection: SalesforceConnection;
  isSelected: boolean;
}

const SalesforceConnectionDropdownItem: React.FC<
  SalesforceConnectionDropdownItemProps
> = ({ connection, isSelected }) => {
  const { name, environmentName } = connection || {};

  return (
  <div className='flex w-full px-3 py-1.5 items-center cursor-pointer h-8'>
    <div className='flex items-center gap-1 w-1/2'>
      <TruncatedText value={name} className='!w-fit' />
      {isSelected && (
        <Icon name='check-circle' className='text-primary-500 text-base' />
      )}
    </div>
      <TruncatedText
        value={environmentName}
        className='pl-2 inline-block !text-secondary-unique-1200 !text-xs !font-medium text-end'
      />
  </div>
);
};

interface SalesforceConnectionSwitcherProps {
  setSelectedConnection: (connection: SalesforceConnection) => void;
}

const SalesforceConnectionSwitcher: React.FC<
  SalesforceConnectionSwitcherProps
> = ({
  setSelectedConnection,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const projectPreference = useContext(UserPreferenceContext).userPreference
    .preference.projectPreference;
  const applicationId = projectPreference?.selectedApplication.id;
  const { t } = useTranslation();


  const isInitialLoad = useRef(true);

  const callbackRef = useCallback((response) => {
    if (isInitialLoad.current) {
      setSelectedConnection(response?.pages?.[0]?.[0]);
      isInitialLoad.current = false; // Mark as executed
    }
  }, []);

  const params = useMemo(() => {
    return {
      query: `applicationId:${applicationId}${
        searchTerm ? `,environmentName:*${searchTerm}*` : ''
      },connectionType:METADATA`,
      size: 20,
      sort: 'id,asc',
      callback: callbackRef 
    };
  }, [applicationId, searchTerm]);

  const {
    data,
    isFetching: isLoading,
    fetchNextPage: handleLoadMore,
    hasNextPage: hasMore,
    refetch,
  } = SalesforceConnectionService.getAllGlobalConnections(params);
  const flattenData: SalesforceConnection[] = useMemo(
    () => data?.pages?.flatMap((item) => item?.content) || [],
    [data],
  );

  const [selectedConnection, updateSelectedConnection] = useState(null);

  // Row renderer for infinite scrolling
  const rowRenderer = useCallback(
    (index: number) => {
      const connection = flattenData[index];
      const isSelected = connection?.id === selectedConnection?.id;

      return (
        <DropdownItem
          className='!p-0'
          key={connection?.name}
          onClick={() => {
            setSelectedConnection(connection);
            updateSelectedConnection(connection);
            setIsOpen(false);
          }}
        >
          <SalesforceConnectionDropdownItem
            connection={connection}
            isSelected={isSelected}
          />
        </DropdownItem>
      );
    },
    [flattenData, selectedConnection],
  );

  const updateSearchTerm = useCallback((val: string) => setSearchTerm(val), []);
  return (
    <DropdownButton
      name={
        selectedConnection ? (
          <div className='mt-1 p-2 flex justify-start cursor-pointer h-9 w-full gap-x-1'>
              <TruncatedText
                value={selectedConnection?.name}
                className='truncate max-w-[50%] flex-shrink-0 !w-fit'
              />
              <TruncatedText
                value={`(${selectedConnection?.environmentName})`}
                className='flex-1 !text-secondary-unique-1200'
              />
          </div>
        ) : (
          <span className=' p-2 flex items-center w-full cursor-pointer h-9 text-secondary-600'>
            {t('select.metadata.connection')}
          </span>
        )
      }
      fillWidth={false}
      wrapperClassName='w-full'
      placement='bottom-start'
      className='w-full bg-transparent h-full border-0.5 border-slate-300 rounded-md rounded-t !bg-secondary-50'
      dropdownClassName='h-[300px] overflow-y-auto'
      hideOnChildClick={false}
      setIsOpen={setIsOpen}
      isOpen={isOpen}
    >
      <div className='h-full w-full overflow-hidden !absolute'>
        <div>
          <DebouncedSearchInput
            value={searchValue}
            className='bg-transparent'
            onChange={updateSearchTerm}
            placeholder={t('common.search')}
            wrapperClassName='outline-none w-full border-0.5 border-secondary-200 !rounded-none'
          />
          <div className='px-3 py-1 flex justify-between items-center bg-secondary-50 font-medium text-small text-unique-600 h-6'>
            <span>{t('connection-name.label')}</span>
            <span>{t('salesforce.environment.label')}</span>
          </div>
        </div>
        {isLoading ? (
          <ListItemSkeleton />
        ) : flattenData?.length ? (
          <VirtualizedInfiniteLoader
            rowHeight={32}
            listHeight={235}
            rowRenderer={rowRenderer}
            rowCount={flattenData?.length}
            items={flattenData}
            loadMore={handleLoadMore}
            hasMore={hasMore}
            isLoading={isLoading}
            LoadingComponent={<ListItemSkeleton />}
          />
        ) : (
          <Empty
            description={
              <span className='text-xs'>{t('no.connections_found')}</span>
            }
            className='-mt-8'
            imageSource={noResultFound}
          />
        )}
      </div>
    </DropdownButton>
  );
};

export default SalesforceConnectionSwitcher;
